import { useState } from 'react'
import { usePermission } from '../../hooks/usePermission'
import {
  getAdminOrder,
  releaseOrderFunds,
  simulateFulfillment,
  simulateTracking,
  type AdminOrderDetail,
} from '../../services/adminApi'

const TRACKING_SEQUENCE = [
  'pre_transit',
  'in_transit',
  'out_for_delivery',
  'delivered',
] as const

type TrackingStatus = (typeof TRACKING_SEQUENCE)[number]

const STATUS_LABELS: Record<string, string> = {
  paid: 'Paid',
  paid_held: 'Paid (Held)',
  completed: 'Completed',
  refund_pending: 'Refund Pending',
  refunded: 'Refunded',
  pending: 'Pending',
}

const FULFILLMENT_LABELS: Record<string, string> = {
  pending: 'Pending',
  shipped: 'Shipped',
  ready_for_pickup: 'Ready for Pickup',
  picked_up: 'Picked Up',
  handed_off: 'Handed Off',
}

const TRACKING_LABELS: Record<string, string> = {
  pre_transit: 'Pre-Transit',
  in_transit: 'In Transit',
  out_for_delivery: 'Out for Delivery',
  delivered: 'Delivered',
  return_to_sender: 'Return to Sender',
  failure: 'Failure',
  cancelled: 'Cancelled',
}

function Badge({ value, map }: { value: string | null | undefined; map: Record<string, string> }) {
  if (!value) return <span className="text-gray-400 text-xs">—</span>
  const colors: Record<string, string> = {
    paid: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    paid_held: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    completed: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    refund_pending: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
    refunded: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    pending: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
    shipped: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
    delivered: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    in_transit: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
    out_for_delivery: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400',
    pre_transit: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  }
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${colors[value] ?? 'bg-gray-100 text-gray-700'}`}
    >
      {map[value] ?? value}
    </span>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
      <span className="text-sm text-gray-900 dark:text-gray-100">{children}</span>
    </div>
  )
}

interface Props {
  order: AdminOrderDetail
  onClose: () => void
  onUpdated: (order: AdminOrderDetail) => void
}

export function OrderDetailModal({ order: initialOrder, onClose, onUpdated }: Props) {
  const [order, setOrder] = useState(initialOrder)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [trackingInput, setTrackingInput] = useState(order.tracking_number ?? '')
  const [carrierInput, setCarrierInput] = useState(order.carrier ?? 'USPS')
  const canSimulate = usePermission('orders', 'simulate')
  const canReleaseFunds = usePermission('orders', 'release_funds')
  const [releaseReason, setReleaseReason] = useState('')
  const [releasing, setReleasing] = useState(false)
  const [releaseError, setReleaseError] = useState<string | null>(null)
  const [releaseNote, setReleaseNote] = useState<string | null>(null)

  const refresh = async () => {
    const res = await getAdminOrder(order.id)
    if (res.ok && res.data) {
      setOrder(res.data)
      onUpdated(res.data)
    }
  }

  const releaseFunds = async () => {
    const reason = releaseReason.trim()
    if (reason.length < 3) return
    if (!window.confirm('Pay the seller now? This sends the held money to their Stripe account and cannot be undone.')) return
    setReleasing(true)
    setReleaseError(null)
    try {
      const res = await releaseOrderFunds(order.id, reason)
      if (!res.ok || !res.data) {
        setReleaseError(res.error?.message ?? 'Release failed')
        return
      }
      const { amount_cents, released_payments, stripe_transfer_id } = res.data
      setReleaseNote(
        amount_cents != null
          ? `Released ${formatCents(amount_cents)}${stripe_transfer_id ? ` (${stripe_transfer_id})` : ''}`
          : `Released ${released_payments} payment${released_payments === 1 ? '' : 's'}`,
      )
      setReleaseReason('')
      await refresh()
    } catch {
      // The money may have moved before the response failed, so check the order before retrying.
      setReleaseError('Could not confirm the release. Reload the order before trying again.')
      await refresh().catch(() => undefined)
    } finally {
      setReleasing(false)
    }
  }

  const doFulfillment = async (status: string, extraFields: Record<string, string> = {}) => {
    setLoading(true)
    setError(null)
    try {
      const res = await simulateFulfillment(order.id, { fulfillment_status: status, ...extraFields })
      if (!res.ok) {
        setError(res.error?.message ?? 'Failed')
      } else {
        await refresh()
      }
    } finally {
      setLoading(false)
    }
  }

  const doTracking = async (status: TrackingStatus) => {
    setLoading(true)
    setError(null)
    try {
      const res = await simulateTracking(order.id, { tracking_status: status })
      if (!res.ok) {
        setError(res.error?.message ?? 'Failed')
      } else {
        await refresh()
      }
    } finally {
      setLoading(false)
    }
  }

  const nextTrackingStatus = (): TrackingStatus | null => {
    const current = order.tracking_status as TrackingStatus | null
    if (!current) return 'pre_transit'
    const idx = TRACKING_SEQUENCE.indexOf(current)
    if (idx === -1 || idx === TRACKING_SEQUENCE.length - 1) return null
    return TRACKING_SEQUENCE[idx + 1]
  }

  const isShipping = order.delivery_type === 'shipped'
  const isPickup = order.delivery_type === 'pickup'
  const isMeetup = order.delivery_type === 'meetup'
  const isShipped = order.fulfillment_status === 'shipped'
  const isDone = order.status === 'completed' || order.status === 'refunded'
  const isPaid = order.status === 'paid' || order.status === 'paid_held'
  const canShip = isPaid && isShipping && order.fulfillment_status === 'pending'
  const canReadyPickup = isPaid && isPickup && order.fulfillment_status === 'pending'
  const canPickedUp = isPaid && order.fulfillment_status === 'ready_for_pickup'
  const canHandOff = isPaid && isMeetup && order.fulfillment_status === 'pending'
  const nextTracking = isShipped ? nextTrackingStatus() : null

  const formatCents = (cents: number | null | undefined) => {
    if (cents == null) return '—'
    return `$${(cents / 100).toFixed(2)}`
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 pt-16 overflow-y-auto">
      <div className="w-full max-w-2xl rounded-2xl bg-white dark:bg-[#1a1425] shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 dark:border-[#3d2c4f] px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Order Detail</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 font-mono mt-0.5">{order.payment_ref}</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-[#2d2236]"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-5 space-y-6">
          {/* Status row */}
          <div className="grid grid-cols-3 gap-4">
            <Field label="Payment Status">
              <Badge value={order.status} map={STATUS_LABELS} />
            </Field>
            <Field label="Fulfillment">
              <Badge value={order.fulfillment_status} map={FULFILLMENT_LABELS} />
            </Field>
            <Field label="Tracking">
              <Badge value={order.tracking_status} map={TRACKING_LABELS} />
            </Field>
          </div>

          {/* Order info */}
          <div className="grid grid-cols-2 gap-4 rounded-xl bg-gray-50 dark:bg-[#120e1b] p-4">
            <Field label="Delivery Type">{order.delivery_type ?? '—'}</Field>
            <Field label="Total">{formatCents(order.total_cents)}</Field>
            <Field label="Item">{order.first_item_name ?? '—'} ({order.item_count} item{order.item_count !== 1 ? 's' : ''})</Field>
            <Field label="Tracking #">{order.tracking_number ?? '—'}</Field>
            <Field label="Carrier">{order.carrier ?? '—'}</Field>
            <Field label="Created">{new Date(order.created_at).toLocaleString()}</Field>
          </div>

          {/* Items */}
          {order.items.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                Items
              </p>
              <div className="space-y-1">
                {order.items.map((item, i) => (
                  <div key={i} className="flex justify-between text-sm py-1 border-b border-gray-100 dark:border-[#2d2236]">
                    <span className="text-gray-800 dark:text-gray-200">
                      {String(item.name ?? item.listing_name ?? 'Unknown')}
                      {Number(item.quantity) > 1 && <span className="text-gray-400 ml-1">×{String(item.quantity)}</span>}
                    </span>
                    <span className="text-gray-500">{formatCents(Number(item.unit_amount_cents) * Number(item.quantity || 1))}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Early payout */}
          {canReleaseFunds && (order.status === 'paid_held' || releaseNote) && (
            <div className="rounded-xl border border-amber-200 dark:border-amber-900/50 bg-amber-50/60 dark:bg-amber-900/10 p-4 space-y-3">
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">Pay the seller now</p>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  Sends the held payment before delivery or pickup. Use only for commission orders. Every release is logged with your reason.
                </p>
              </div>
              {releaseNote ? (
                <p className="text-sm text-green-700 dark:text-green-400">{releaseNote}</p>
              ) : (
                <>
                  <textarea
                    value={releaseReason}
                    onChange={(e) => setReleaseReason(e.target.value)}
                    placeholder="Why is this being released early?"
                    rows={2}
                    maxLength={500}
                    className="w-full rounded-lg border border-gray-200 dark:border-[#3d2c4f] bg-white dark:bg-[#120e1b] px-3 py-2 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#7f4f9a]"
                  />
                  {releaseError && <p className="text-sm text-red-600 dark:text-red-400">{releaseError}</p>}
                  <button
                    onClick={releaseFunds}
                    disabled={releasing || releaseReason.trim().length < 3}
                    className="rounded-lg bg-[#503864] px-4 py-2 text-sm font-medium text-white hover:bg-[#3f2b50] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {releasing ? 'Releasing…' : 'Release payment'}
                  </button>
                </>
              )}
            </div>
          )}

          {/* Simulation panel */}
          {canSimulate && !isDone && (
            <div className="rounded-xl border-2 border-dashed border-[#b58fcc] dark:border-[#7f4f9a] p-4 space-y-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-[#7f4f9a] dark:text-[#b58fcc]">
                Simulate State Transitions
              </p>

              {error && (
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              )}

              {/* Ship action */}
              {canShip && (
                <div className="space-y-2">
                  <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">Mark as Shipped</p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Tracking number"
                      value={trackingInput}
                      onChange={(e) => setTrackingInput(e.target.value)}
                      className="flex-1 rounded-lg border border-gray-200 dark:border-[#3d2c4f] bg-white dark:bg-[#120e1b] px-3 py-1.5 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#7f4f9a]"
                    />
                    <select
                      value={carrierInput}
                      onChange={(e) => setCarrierInput(e.target.value)}
                      className="rounded-lg border border-gray-200 dark:border-[#3d2c4f] bg-white dark:bg-[#120e1b] px-3 py-1.5 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#7f4f9a]"
                    >
                      <option>USPS</option>
                      <option>UPS</option>
                      <option>FedEx</option>
                      <option>DHL</option>
                    </select>
                    <button
                      disabled={loading}
                      onClick={() =>
                        doFulfillment('shipped', {
                          tracking_number: trackingInput || `SIM-${Date.now()}`,
                          carrier: carrierInput,
                        })
                      }
                      className="rounded-lg bg-[#503864] hover:bg-[#7f4f9a] text-white px-4 py-1.5 text-sm font-medium disabled:opacity-50 transition-colors"
                    >
                      Ship
                    </button>
                  </div>
                </div>
              )}

              {/* Pickup actions */}
              {canReadyPickup && (
                <button
                  disabled={loading}
                  onClick={() => doFulfillment('ready_for_pickup')}
                  className="rounded-lg bg-[#503864] hover:bg-[#7f4f9a] text-white px-4 py-2 text-sm font-medium disabled:opacity-50 transition-colors w-full"
                >
                  Mark Ready for Pickup
                </button>
              )}
              {canPickedUp && (
                <button
                  disabled={loading}
                  onClick={() => doFulfillment('picked_up')}
                  className="rounded-lg bg-green-600 hover:bg-green-700 text-white px-4 py-2 text-sm font-medium disabled:opacity-50 transition-colors w-full"
                >
                  Mark Picked Up → Complete Order
                </button>
              )}

              {/* Meetup */}
              {canHandOff && (
                <button
                  disabled={loading}
                  onClick={() => doFulfillment('handed_off')}
                  className="rounded-lg bg-green-600 hover:bg-green-700 text-white px-4 py-2 text-sm font-medium disabled:opacity-50 transition-colors w-full"
                >
                  Mark Handed Off → Complete Order
                </button>
              )}

              {/* Tracking advancement */}
              {nextTracking && (
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 font-medium mb-2">
                    Advance Tracking
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {TRACKING_SEQUENCE.map((status) => {
                      const currentIdx = order.tracking_status
                        ? TRACKING_SEQUENCE.indexOf(order.tracking_status as TrackingStatus)
                        : -1
                      const thisIdx = TRACKING_SEQUENCE.indexOf(status)
                      const isActive = status === order.tracking_status
                      const isPast = thisIdx <= currentIdx
                      const isNext = status === nextTracking
                      return (
                        <button
                          key={status}
                          disabled={loading || (!isNext && !isActive)}
                          onClick={() => isNext && doTracking(status)}
                          className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                            isActive
                              ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300 ring-2 ring-indigo-400'
                              : isPast
                              ? 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-600 cursor-default'
                              : isNext
                              ? 'bg-[#503864] text-white hover:bg-[#7f4f9a] cursor-pointer'
                              : 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-600 cursor-default'
                          }`}
                        >
                          {TRACKING_LABELS[status]}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}

              {isDone && (
                <p className="text-sm text-gray-500 dark:text-gray-400 italic">Order is completed.</p>
              )}
            </div>
          )}

          {/* Shipping address */}
          {order.shipping_address && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                Ship To
              </p>
              <pre className="text-xs bg-gray-50 dark:bg-[#120e1b] rounded-lg p-3 text-gray-700 dark:text-gray-300 overflow-x-auto">
                {JSON.stringify(order.shipping_address, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
